/**
 * referral-capture.js — Enrichment custom script (not a Sleek theme file).
 * Sitewide, dependency-free FIRST step of referral attribution: captures
 * a `?ref=CODE` URL param into a cookie on any page load, regardless of
 * login state. Loaded unconditionally on EVERY storefront page (see
 * layout/theme.liquid, right alongside theme.js) because a shared
 * referral link can land anywhere -- a specific product page, a
 * collection, the homepage -- not just a page that already knows about
 * registration or accounts.
 *
 * WHY THIS EXISTS AS A SEPARATE FILE FROM referral-attribution.js: this
 * script never calls the Worker and never reads `customer` -- it runs the
 * same way for a logged-out browser as a logged-in one, because the
 * referral link click and the eventual account creation are frequently
 * two separate visits (browse now, register/checkout later). The actual
 * attribution call -- linking a captured code to a specific authenticated
 * customer -- happens later and separately; see referral-attribution.js
 * and workers/loyalty/src/index.js's `POST /apps/rewards/
 * attribute-referral` (search that file for "REFERRAL ATTRIBUTION
 * MECHANISM" for the full reasoning, including why this two-step,
 * cookie-then-authenticated-call design replaced an earlier tag-based
 * approach that turned out to be structurally impossible).
 *
 * FIRST-TOUCH, NOT LAST-TOUCH: once a referral cookie is set, this file
 * never overwrites it, even if the same browser later loads a page
 * carrying a DIFFERENT `?ref=` value. A referral link is meant to credit
 * whoever the visitor genuinely followed first; without this guard, any
 * later, unrelated link visit (a different friend's share of some other
 * page, a re-shared social post) could silently steal an earlier, real
 * referral from the person who actually sent them.
 *
 * 30-DAY EXPIRY: a standard, reasonable "did they convert because of this
 * link" attribution window -- long enough to cover someone bookmarking a
 * link and coming back later, short enough that a years-old, long-forgotten
 * link isn't still silently attributing new signups. (This happens to
 * match the loyalty programme's own 30-day referral hold window in
 * workers/loyalty/src/index.js's REFERRAL_HOLD_DAYS, but the two are
 * independent constants answering different questions -- one is "how long
 * do we trust this click", the other is "how long do we wait before
 * paying out a qualified referral" -- and are not meant to be kept in
 * sync if either ever changes.)
 */
(function () {
  var COOKIE_NAME = 'enrichment_referral_code';
  var COOKIE_MAX_AGE_DAYS = 30;

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, maxAgeDays) {
    var maxAgeSeconds = maxAgeDays * 24 * 60 * 60;
    document.cookie =
      name + '=' + encodeURIComponent(value) + '; max-age=' + maxAgeSeconds + '; path=/; SameSite=Lax';
  }

  var refCode = new URLSearchParams(window.location.search).get('ref');
  if (!refCode) return;

  // First-touch guard -- see file header. A page carrying `?ref=` with no
  // existing cookie is the only case this script ever writes anything.
  if (getCookie(COOKIE_NAME)) return;

  setCookie(COOKIE_NAME, refCode, COOKIE_MAX_AGE_DAYS);
})();
